/**
 * Página del ALUMNO: mis tareas por cohorte (F3-B) — pendientes/entregadas y
 * calificadas, con fecha de entrega y atajo para entregar. Local-first sobre los
 * streams my_cohorts_student (cohorte, ajustes por módulo, mis entregas/notas) +
 * el árbol del curso (assignment) que ya sincroniza el catálogo/org.
 */
import { createRoute } from "@tanstack/solid-router";
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, Clock, FileText } from "lucide-solid";
import { gradingScheme } from "@space/shared";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCohort,
  useCohortModuleSettings,
  useMyCohortEnrollments,
} from "@/features/academy/hooks/use-cohort-data";
import { useCourseModules } from "@/features/academy/hooks/use-course-modules";
import {
  useCourseAssignments,
  useMyGrades,
  useMySubmissions,
} from "@/features/academy/hooks/use-assessment";

const AssignmentsPage: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();
  const enrollments = useMyCohortEnrollments();
  const activeCohorts = createMemo(() =>
    (enrollments.data ?? []).filter((e) => e.status === "active" || e.status === "completed"),
  );

  return (
    <div class="mx-auto max-w-3xl space-y-5 p-4 md:p-6">
      <h1 class="font-display text-2xl font-extrabold">{t(msg`My assignments`)}</h1>
      <Show when={!enrollments.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
        <Show
          when={activeCohorts().length > 0}
          fallback={
            <p class="text-muted-foreground" data-testid="assignments-empty">
              {t(msg`You have no active cohorts yet.`)}
            </p>
          }
        >
          <For each={activeCohorts()}>
            {(e) => <CohortAssignments cohortId={e.cohort_id ?? ""} userId={userId()} />}
          </For>
        </Show>
      </Show>
    </div>
  );
};

const CohortAssignments: Component<{ cohortId: string; userId: string }> = (props) => {
  const { t } = useI18n();
  const cohort = useCohort(() => props.cohortId);
  const courseId = createMemo(() => cohort.data?.course_id ?? undefined);
  const assignments = useCourseAssignments(() => courseId());
  const modules = useCourseModules(() => courseId());
  const settings = useCohortModuleSettings(() => props.cohortId);
  const submissions = useMySubmissions(() => props.userId);
  const grades = useMyGrades(() => props.userId);

  // due_at por assignment: cohort_module_setting del course_module (kind=assignment).
  const dueByAssignment = createMemo(() => {
    const map = new Map<string, string>();
    const moduleByRef = new Map<string, string>();
    for (const m of modules.data ?? []) {
      if (m.kind === "assignment" && m.ref_id) {
        moduleByRef.set(m.id, m.ref_id);
      }
    }
    for (const s of settings.data ?? []) {
      const ref = s.module_id ? moduleByRef.get(s.module_id) : undefined;
      if (ref && s.due_at) {
        map.set(ref, s.due_at);
      }
    }
    return map;
  });

  const statusFor = (assignmentId: string) => {
    const grade = (grades.data ?? []).find(
      (g) => g.assignment_id === assignmentId && g.cohort_id === props.cohortId,
    );
    if (grade && (grade.status === "graded" || grade.status === "returned")) {
      return { kind: "graded" as const, grade };
    }
    const sub = (submissions.data ?? []).find(
      (s) => s.assignment_id === assignmentId && s.cohort_id === props.cohortId,
    );
    if (sub) {
      return { kind: "submitted" as const, late: Boolean(sub.late) };
    }
    return { kind: "pending" as const };
  };

  return (
    <Show when={(assignments.data ?? []).length > 0}>
      <section
        class="space-y-2 rounded-2xl border border-border p-4"
        data-testid="cohort-assignments"
      >
        <h2 class="font-bold">{cohort.data?.name ?? t(msg`Cohort`)}</h2>
        <For each={assignments.data ?? []}>
          {(a) => {
            const st = statusFor(a.id);
            const due = dueByAssignment().get(a.id);
            return (
              <div
                class="flex items-center justify-between rounded-xl bg-muted/30 p-3"
                data-testid="assignment-item"
              >
                <div class="flex items-center gap-2">
                  <FileText class="h-4 w-4 text-accent" />
                  <div>
                    <div class="text-sm font-semibold">{a.title}</div>
                    <Show when={due}>
                      <div class="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock class="h-3 w-3" /> {t(msg`Due`)} {formatDate(due!)}
                      </div>
                    </Show>
                  </div>
                </div>
                <div class="text-xs font-bold">
                  <Show when={st.kind === "pending"}>
                    <span
                      class="rounded-full bg-joy-berry/15 px-2 py-0.5 text-joy-berry"
                      data-testid="assignment-status-pending"
                    >
                      {t(msg`Pending`)}
                    </span>
                  </Show>
                  <Show when={st.kind === "submitted"}>
                    <span
                      class="rounded-full bg-accent/12 px-2 py-0.5 text-accent"
                      data-testid="assignment-status-submitted"
                    >
                      {t(msg`Submitted`)}
                    </span>
                  </Show>
                  <Show when={st.kind === "graded"}>
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-joy-mint/15 px-2 py-0.5 text-joy-mint"
                      data-testid="assignment-status-graded"
                    >
                      <CheckCircle2 class="h-3 w-3" />
                      {formatScore(
                        st.kind === "graded" ? (st.grade.score ?? null) : null,
                        a.max_score ?? null,
                      )}
                    </span>
                  </Show>
                </div>
              </div>
            );
          }}
        </For>
      </section>
    </Show>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function formatScore(score: number | null, max: number | null): string {
  if (score == null) {
    return "—";
  }
  const raw = max && max > 0 ? score / max : 0;
  return gradingScheme("ve-0-20").toDisplay(raw);
}

export const assignmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assignments",
  component: AssignmentsPage,
});
